import React from "react";
import { motion } from "framer-motion";
import {
  SiReact, SiNextdotjs, SiWordpress, SiJavascript, SiTypescript,
  SiHtml5, SiCss, SiNodedotjs, SiTailwindcss, SiFigma, SiMongodb,
  SiDocker, SiKubernetes, SiVercel, SiPython, SiFlutter, SiFirebase,
  SiGraphql, SiPhp
} from "react-icons/si";
import { FaAmazon } from "react-icons/fa";
import { technologies } from "../data/mock";

const iconMap = {
  "AWS": <FaAmazon className="text-[#FF9900]" />,
  "React": <SiReact className="text-[#61DAFB]" />,
  "Next.js": <SiNextdotjs className="text-white" />,
  "WordPress": <SiWordpress className="text-[#21759B]" />,
  "JavaScript": <SiJavascript className="text-[#F7DF1E]" />,
  "TypeScript": <SiTypescript className="text-[#3178C6]" />,
  "HTML5": <SiHtml5 className="text-[#E34F26]" />,
  "CSS3": <SiCss className="text-[#1572B6]" />,
  "Node.js": <SiNodedotjs className="text-[#339933]" />,
  "Tailwind CSS": <SiTailwindcss className="text-[#06B6D4]" />,
  "Figma": <SiFigma className="text-[#F24E1E]" />,
  "MongoDB": <SiMongodb className="text-[#47A248]" />,
  "Docker": <SiDocker className="text-[#2496ED]" />,
  "Kubernetes": <SiKubernetes className="text-[#326CE5]" />,
  "Vercel": <SiVercel className="text-white" />,
  "Python": <SiPython className="text-[#3776AB]" />,
  "Flutter": <SiFlutter className="text-[#02569B]" />,
  "Firebase": <SiFirebase className="text-[#FFCA28]" />,
  "GraphQL": <SiGraphql className="text-[#E10098]" />,
  "PHP": <SiPhp className="text-[#777BB4]" />
};

const Technologies = () => {
  return (
    <section id="technologies" className="section relative overflow-hidden bg-[#0a0908]">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(var(--rule) 1px, transparent 1px), linear-gradient(90deg, var(--rule) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      {/* Subtle Glows */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[var(--gold)] opacity-[0.05] blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[var(--gold)] opacity-[0.05] blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10">
        <div className="container-xl mb-16">
          <div className="text-left mb-10">
            <Kicker index="07" label="Digital Arsenal" />
          </div>

          <div className="text-center">
            <h2 className="text-[clamp(32px,4vw,56px)] font-display uppercase leading-tight mb-4">
              Our <span className="gold-text italic">Tech Stack</span>
            </h2>
            <div className="w-20 h-1 bg-[var(--gold)] mx-auto mb-6" />
            <p className="text-[var(--cream-muted)] max-w-2xl mx-auto text-lg font-light balance mb-6">
              We leverage the most advanced and reliable technologies to build scalable,
              high-performance digital solutions that drive business growth.
            </p>
          </div>
        </div>

        {/* 4-Row Infinite Tech Stream (Mobile - Edge to Edge) */}
        <div className="md:hidden flex flex-col gap-4 py-8 overflow-hidden">
          {[0, 1, 2, 3].map((rowIdx) => (
            <div key={`row-${rowIdx}`} className="relative flex">
              <motion.div
                animate={{
                  x: rowIdx % 2 === 0 ? ["0%", "-50%"] : ["-50%", "0%"]
                }}
                transition={{
                  duration: 20 + rowIdx * 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="flex gap-4 whitespace-nowrap"
              >
                {/* Duplicate technologies to create seamless loop */}
                {[...technologies, ...technologies].map((tech, i) => (
                  <motion.div
                    key={`${rowIdx}-${tech.name}-${i}`}
                    className="shrink-0 flex items-center gap-3 px-6 py-4 rounded-2xl bg-[#14110f]/80 border border-white/10 shadow-lg backdrop-blur-md"
                  >
                    <div className="text-2xl text-[var(--gold)]">
                      {iconMap[tech.name] || <SiReact />}
                    </div>
                    <span className="text-[10px] font-impact tracking-[0.2em] text-[var(--cream-muted)] uppercase">
                      {tech.name}
                    </span>
                  </motion.div>
                ))}
              </motion.div>

              {/* Edge Fades for the marquee */}
              <div className="absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-[#0a0908] to-transparent z-10" />
              <div className="absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-[#0a0908] to-transparent z-10" />
            </div>
          ))}
        </div>

        {/* Desktop Version (Contained) */}
        <div className="container-xl hidden md:grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
          {technologies.map((tech, i) => (
            <TechCard key={tech.name} tech={tech} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const TechCard = ({ tech, index }) => {
  return (
    <motion.div
      whileHover="hover"
      className="group relative flex flex-col items-center justify-center p-6 md:p-8 rounded-3xl bg-[#14110f]/60 border border-white/5 hover:border-[var(--gold)]/50 transition-all duration-500 overflow-hidden shadow-2xl"
      style={{ backdropFilter: "blur(12px)" }}
    >
      {/* Organic Liquid Blobs (Water/Bubble Effect) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          variants={{
            hover: {
              scale: [1, 1.2, 1.1, 1.3, 1],
              x: [0, 10, -10, 5, 0],
              y: [0, -15, 10, -5, 0],
              opacity: 0.15
            }
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          className="absolute -top-1/2 -left-1/2 w-full h-full bg-[var(--gold)] blur-[60px] rounded-full opacity-0"
        />
        <motion.div
          variants={{
            hover: {
              scale: [1, 1.3, 1.1, 1.2, 1],
              x: [0, -10, 15, -5, 0],
              y: [0, 10, -15, 5, 0],
              opacity: 0.1
            }
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-[var(--copper)] blur-[60px] rounded-full opacity-0"
        />
      </div>

      {/* Center Ripple/Bubble */}
      <motion.div
        variants={{
          hover: {
            scale: [0.8, 1.5],
            opacity: [0, 0.2, 0]
          }
        }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
        className="absolute inset-0 bg-white rounded-full blur-[40px] opacity-0 pointer-events-none"
      />

      {/* Inner Soft Glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background: "radial-gradient(circle at center, rgba(228,199,122,0.15) 0%, transparent 80%)"
        }}
      />

      <div className="relative z-10 flex flex-col items-center">
        <motion.div
          className="text-4xl md:text-5xl mb-5 filter drop-shadow-sm group-hover:drop-shadow-[0_0_15px_rgba(201,169,97,0.5)] group-hover:scale-110 transition-all duration-700"
          animate={{
            y: [0, -5, 0],
          }}
          transition={{
            duration: 4 + (index % 2),
            repeat: Infinity,
            ease: "easeInOut",
            delay: index * 0.1
          }}
        >
          {iconMap[tech.name] || <SiReact />}
        </motion.div>

        <h3 className="text-[11px] md:text-[12px] font-impact tracking-[0.3em] text-[var(--cream-muted)] group-hover:text-[var(--gold-bright)] transition-colors duration-500 uppercase text-center mb-1">
          {tech.name}
        </h3>

        <div className="h-[2px] w-0 group-hover:w-16 bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent transition-all duration-700" />
      </div>

      {/* Prominent Golden Border Reveal */}
      <div className="absolute inset-0 border-[1.5px] border-[var(--gold)]/40 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none shadow-[inset_0_0_20px_rgba(201,169,97,0.1)]" />
    </motion.div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default Technologies;
